import { Grid } from "@mui/material";
import { useTheme } from "@mui/styles";
import {
  B2C_ONBOARDING_PAGES,
  WESER_EMS_AOK_PARTNER_LOGO,
  WESER_EMS_AOK_PARTNER_URL,
  WESER_EMS_BKK_PARTNER_LOGO,
  WESER_EMS_BKK_PARTNER_URL,
  WESER_EMS_CONSULTATION_URL,
  WESER_EMS_LOWER_SAXONY_MINISTRY_URL,
  WESER_EMS_VDEK_PARTNER_LOGO,
  WESER_EMS_VDEK_PARTNER_URL,
} from "core/consts";
import LinkV2 from "ds_legacy/components/LinkV2";
import { B2C_PRIMARY_COLOR, hexWithOpacity } from "ds_legacy/materials/colors";
import { HorizontalLayout, VerticalLayout } from "ds_legacy/materials/layouts";
import { margin, padding, sizing } from "ds_legacy/materials/metrics";
import {
  Body,
  Caption,
  FONT_SIZE_14,
  FONT_SIZE_16,
  FONT_SIZE_18,
  FONT_WEIGHT_BOLD,
  FONT_WEIGHT_REGULAR,
  FONT_WEIGHT_SEMI_BOLD,
  Subheading,
} from "ds_legacy/materials/typography";
import Image from "dsl/atoms/Image";
import { useB2CNavigationUrls } from "dsl/hooks/useNavigationHandlers";
import { ArrowLeftIcon } from "lucide-react";
import React, { CSSProperties, ReactNode } from "react";
import { useTranslations } from "translations";
import { TranslationComposition } from "translations/helpers";
import Translations from "translations/types";
import { PageWrapper, testIds } from "../common";

const LIST_STYLE = { margin: margin(0, 0, 2), padding: padding(0, 0, 0, 4) };

const BodyText = ({
  children,
  fontSize = FONT_SIZE_14,
  fontWeight = FONT_WEIGHT_REGULAR,
  noMargin,
}: {
  children: ReactNode;
  fontSize?: CSSProperties["fontSize"];
  fontWeight?: CSSProperties["fontWeight"];
  noMargin?: boolean;
}) => {
  return (
    <Body
      as="p"
      fontSize={fontSize}
      fontWeight={fontWeight}
      margin={noMargin ? margin(0) : margin(0, 0, 2)}
    >
      {children}
    </Body>
  );
};

const BackLink = () => {
  const theme = useTheme();
  const translations = useTranslations();
  const { goToOnboarding } = useB2CNavigationUrls();

  return (
    <>
      <LinkV2
        color="default"
        data-testid="back-link"
        id="back"
        sx={{
          color: theme.palette.primary.main,
          cursor: "pointer",
          padding: padding(3, 0),
        }}
        to={goToOnboarding({ page: B2C_ONBOARDING_PAGES.CARE_SOLUTION })}
      >
        <HorizontalLayout aligned as="span" overflow="visible">
          <ArrowLeftIcon
            size={FONT_SIZE_16}
            style={{ fontSize: FONT_SIZE_16, margin: margin(0, 1, 0, 0) }}
          />
          {translations.providersearch.providerDetailsPage.backToListing}
        </HorizontalLayout>
      </LinkV2>
    </>
  );
};

const InformationSection = () => {
  const translations = useTranslations();
  return (
    <>
      <Subheading
        as="h3"
        style={{
          fontSize: FONT_SIZE_18,
          fontWeight: FONT_WEIGHT_BOLD,
          margin: margin(0, 0, 3),
        }}
      >
        {translations.bToC.onboardingScreens.dailyLivingSupportServices.title}
      </Subheading>
      <BodyText>
        {
          translations.bToC.onboardingScreens.dailyLivingSupportServices
            .pageContent.paragraphOne
        }
      </BodyText>
      <BodyText>
        {
          translations.bToC.onboardingScreens.dailyLivingSupportServices
            .pageContent.paragraphTwo
        }
      </BodyText>
      <BodyText>
        {
          translations.bToC.onboardingScreens.dailyLivingSupportServices
            .pageContent.paragraphThree
        }
      </BodyText>
      <ul style={LIST_STYLE}>
        <li>
          <BodyText noMargin>
            {
              translations.bToC.onboardingScreens.dailyLivingSupportServices
                .pageContent.bulletOne
            }
          </BodyText>
        </li>
        <li>
          <BodyText noMargin>
            {
              translations.bToC.onboardingScreens.dailyLivingSupportServices
                .pageContent.bulletTwo
            }
          </BodyText>
        </li>
        <li>
          <BodyText noMargin>
            {
              translations.bToC.onboardingScreens.dailyLivingSupportServices
                .pageContent.bulletThree
            }
          </BodyText>
        </li>
        <li>
          <BodyText noMargin>
            {
              translations.bToC.onboardingScreens.dailyLivingSupportServices
                .pageContent.bulletFour
            }
          </BodyText>
        </li>
      </ul>
      <BodyText>
        {
          translations.bToC.onboardingScreens.dailyLivingSupportServices
            .pageContent.paragraphFour
        }
      </BodyText>
      <ul style={LIST_STYLE}>
        <li>
          <BodyText noMargin>
            {
              translations.bToC.onboardingScreens.dailyLivingSupportServices
                .pageContent.bulletFive
            }
          </BodyText>
        </li>
        <li>
          <BodyText noMargin>
            {
              translations.bToC.onboardingScreens.dailyLivingSupportServices
                .pageContent.bulletSix
            }
          </BodyText>
        </li>
        <li>
          <BodyText noMargin>
            {
              translations.bToC.onboardingScreens.dailyLivingSupportServices
                .pageContent.bulletSeven
            }
          </BodyText>
        </li>
        <li>
          <BodyText noMargin>
            {
              translations.bToC.onboardingScreens.dailyLivingSupportServices
                .pageContent.bulletEight
            }
          </BodyText>
        </li>
      </ul>
      <TranslationComposition
        translations={
          translations.bToC.onboardingScreens.dailyLivingSupportServices
            .pageContent.paragraphFive
        }
        withOptions
      >
        {([before, link, after]) => (
          <BodyText>
            {before}
            <LinkV2
              href={WESER_EMS_LOWER_SAXONY_MINISTRY_URL}
              rel="noopener noreferrer"
              target="_blank"
            >
              {link}
            </LinkV2>
            {after}
          </BodyText>
        )}
      </TranslationComposition>
    </>
  );
};

type LogoMapItem = {
  image: string;
  label: string;
  link: string;
};

type LogoMap = {
  [key: string]: (translations: Translations) => LogoMapItem;
};

const LOGO_MAP: LogoMap = {
  aok: (translations: Translations) => ({
    image: WESER_EMS_AOK_PARTNER_LOGO,
    label:
      translations.bToC.onboardingScreens.dailyLivingSupportServices.pageContent
        .linkAok,
    link: WESER_EMS_AOK_PARTNER_URL,
  }),
  bkk: (translations: Translations) => ({
    image: WESER_EMS_BKK_PARTNER_LOGO,
    label:
      translations.bToC.onboardingScreens.dailyLivingSupportServices.pageContent
        .linkBkk,
    link: WESER_EMS_BKK_PARTNER_URL,
  }),
  vdek: (translations: Translations) => ({
    image: WESER_EMS_VDEK_PARTNER_LOGO,
    label:
      translations.bToC.onboardingScreens.dailyLivingSupportServices.pageContent
        .linkVdek,
    link: WESER_EMS_VDEK_PARTNER_URL,
  }),
};

const ProviderLinks = () => {
  const theme = useTheme();
  const translations = useTranslations();
  const items = Object.keys(LOGO_MAP).map((key) => LOGO_MAP[key](translations));
  return (
    <Grid
      columnGap={1}
      container
      rowGap={1}
      sx={{
        backgroundColor: theme.palette.common.white,
        margin: margin(1.25, 0),
        padding: padding(1.5),
      }}
    >
      {items.map((item, index) => (
        <React.Fragment key={index}>
          <Grid item xs={6}>
            <LinkV2
              href={item.link}
              sx={{ fontSize: FONT_SIZE_14 }}
              target="_blank"
            >
              {item.label}
            </LinkV2>
          </Grid>
          <Grid item xs={5}>
            <Image
              alt={`Logo ${item.label}`}
              path={item.image}
              style={{ height: sizing(3) }}
            />
          </Grid>
        </React.Fragment>
      ))}
    </Grid>
  );
};

const ProviderSection = () => {
  const translations = useTranslations();
  return (
    <div
      style={{
        backgroundColor: hexWithOpacity(B2C_PRIMARY_COLOR, 10),
        margin: margin(1, 0, 3),
        padding: padding(2),
      }}
    >
      <Subheading
        as="h4"
        style={{
          fontSize: FONT_SIZE_16,
          fontWeight: FONT_WEIGHT_BOLD,
          margin: margin(0, 0, 3),
        }}
      >
        {
          translations.bToC.onboardingScreens.dailyLivingSupportServices
            .pageContent.bannerTitle
        }
      </Subheading>
      <TranslationComposition
        translations={
          translations.bToC.onboardingScreens.dailyLivingSupportServices
            .pageContent.bannerInformation
        }
        withOptions
      >
        {([before, link, after]) => (
          <BodyText>
            {before}
            <LinkV2
              href={WESER_EMS_CONSULTATION_URL}
              rel="noopener noreferrer"
              target="_blank"
            >
              {link}
            </LinkV2>
            {after}
          </BodyText>
        )}
      </TranslationComposition>
      <BodyText fontWeight={FONT_WEIGHT_SEMI_BOLD} noMargin>
        {
          translations.bToC.onboardingScreens.dailyLivingSupportServices
            .pageContent.bannerSubtitle
        }
      </BodyText>
      <ProviderLinks />
      <Caption
        margin={margin(0)}
        style={{ fontWeight: FONT_WEIGHT_BOLD }}
        whiteSpace="normal"
      >
        {
          translations.bToC.onboardingScreens.dailyLivingSupportServices
            .pageContent.hintText
        }
      </Caption>
    </div>
  );
};

export default function DailyLivingSupportServices() {
  return (
    <PageWrapper data-testid={testIds.dailyLivingSupportServices}>
      <VerticalLayout>
        <BackLink />
        <InformationSection />
        <ProviderSection />
      </VerticalLayout>
    </PageWrapper>
  );
}
